import React, { useState, useRef } from 'react'
import { ShieldAlert, Search, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react'
import { guestPlayerService } from '@/services/guestPlayerService'

interface BreachCheckActivityProps {
  activityData: Record<string, any>
  disabled: boolean
  runCode: string
  onSubmitResponse: (data: Record<string, any>) => Promise<{ success: boolean; error?: string }>
  onComplete?: () => void
}

interface Breach {
  Name: string
  BreachDate: string
  DataClasses: string[]
  Description: string
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/**
 * Format an HIBP breach date (YYYY-MM-DD) as a US-style long date,
 * e.g. "2026-07-04" -> "July 4, 2026". Parsed manually to avoid the
 * timezone shift that new Date('YYYY-MM-DD') can introduce. Falls back
 * to the raw value if it isn't in the expected format.
 */
const formatBreachDate = (raw: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw || '')
  if (!match) return raw || ''
  const [, year, month, day] = match
  const monthName = MONTH_NAMES[Number(month) - 1]
  if (!monthName) return raw
  return `${monthName} ${Number(day)}, ${Number(year)}`
}

export function BreachCheckActivity({ activityData, disabled, runCode, onSubmitResponse, onComplete }: BreachCheckActivityProps) {
  const [email, setEmail] = useState('')
  const [checking, setChecking] = useState(false)
  const [breaches, setBreaches] = useState<Breach[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const submittedRef = useRef(false)

  const handleCheck = async () => {
    if (!email.trim() || checking) return
    setChecking(true)
    setError(null)
    setBreaches(null)

    try {
      const result = await guestPlayerService.hibpBreachCheck(runCode, email.trim())
      if (result.success && result.data) {
        const found = result.data.breaches || []
        setBreaches(found)
        void recordParticipation(found.length)
      } else {
        setError(result.error || 'Failed to check breaches')
      }
    } catch {
      setError('Failed to check breaches')
    } finally {
      setChecking(false)
    }
  }

  // Record participation once, on the player's first check. Kept separate from
  // the "Check" action so players can keep experimenting with other emails
  // without pressing a button to "finish".
  const recordParticipation = async (breachCount: number) => {
    if (submittedRef.current) return
    submittedRef.current = true
    const result = await onSubmitResponse({
      email_checked: true,
      breach_count: breachCount,
    })
    if (result.success) setSubmitted(true)
    else submittedRef.current = false
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text)]">
        <ShieldAlert className="w-5 h-5 text-rose-500" />
        Breach Check
      </div>

      {activityData?.instruction && (
        <p className="text-sm text-[var(--color-text-muted)]">{activityData.instruction}</p>
      )}

      <p className="text-sm text-[var(--color-text-muted)]">
        Check if an email address has appeared in known data breaches using the Have I Been Pwned database.
      </p>

      {/* Email input — stays available so players can check multiple emails */}
      {!disabled && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[var(--color-text)] mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter an email to check..."
              className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm text-[var(--color-text)] bg-transparent"
              onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
            />
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Your email is only sent to the HIBP API and is never stored.
            </p>
          </div>
          <button
            onClick={handleCheck}
            disabled={!email.trim() || checking}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 disabled:opacity-50 text-sm font-medium"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {checking ? 'Checking...' : 'Check Breaches'}
          </button>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Results */}
      {breaches !== null && (
        <div className="space-y-3">
          {breaches.length === 0 ? (
            <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-500 shrink-0" />
              <div>
                <p className="font-medium text-green-400">No breaches found!</p>
                <p className="text-sm text-[var(--color-text-muted)]">This email was not found in any known data breaches.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 p-3 bg-rose-500/10 border border-rose-500/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0" />
                <p className="font-medium text-rose-400">
                  Found in {breaches.length} {breaches.length === 1 ? 'breach' : 'breaches'}
                </p>
              </div>

              <div className="space-y-2">
                {breaches.map((breach) => (
                  <div key={breach.Name} className="p-3 border border-[var(--color-border)] rounded-lg bg-[var(--color-surface)]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-[var(--color-text)]">{breach.Name}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">{formatBreachDate(breach.BreachDate)}</span>
                    </div>
                    {breach.DataClasses && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {breach.DataClasses.slice(0, 5).map((dc) => (
                          <span key={dc} className="text-xs px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20">
                            {dc}
                          </span>
                        ))}
                        {breach.DataClasses.length > 5 && (
                          <span className="text-xs text-[var(--color-text-muted)]">+{breach.DataClasses.length - 5} more</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {submitted && onComplete && (
            <button
              onClick={onComplete}
              className="w-full py-2.5 rounded-lg font-semibold text-sm text-white"
              style={{ background: 'var(--gradient-primary)' }}
            >
              Next Activity
            </button>
          )}
        </div>
      )}

      {disabled && breaches === null && (
        <div className="p-4 text-center text-[var(--color-text-muted)]">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <p className="font-medium">Already completed</p>
        </div>
      )}
    </div>
  )
}
